import {
  ColumnDef,
  ExpandedState,
  functionalUpdate,
  GroupingState,
  InitialTableState,
  PaginationState,
  RowSelectionState,
  SortingState,
  Updater,
} from '@tanstack/react-table';
import { useCallback, useEffect, useMemo, useState } from 'react';
import isEqual from 'react-fast-compare';
import { createAggregatedListing } from 'src/components/Listings/listingUtils';
import { useActiveSortTableColumnContext } from 'src/contexts/ActiveSortTableColumnContext';
import { useAppContext } from 'src/contexts/AppContext';
import { ColumnResizingContextProvider } from 'src/contexts/ColumnResizingContext/ColumnResizingContext';
import { Content, FormatContent } from 'src/contexts/ContentContext';
import { useInputPriceFocusContext } from 'src/contexts/InputPriceFocusContext/InputPriceFocusContext';
import {
  MultiSelectScope,
  NO_GROUP_ID,
  SelectionMode,
  useMultiSelectionContext,
} from 'src/contexts/MultiSelectionContext';
import { useClearShiftSelectionOnSortingChange } from 'src/contexts/MultiSelectionContext/useClearShiftSelectionOnSortingChange';
import { useSearchDataContextWithCatalogFallback } from 'src/contexts/SearchDataContext';
import { WarningMessage } from 'src/core/POS/MessageWithIcon';
import { useSortListingTableSeatingColumn } from 'src/hooks/useSortListingTableSeatingColumn';
import { useTagsForEntityType } from 'src/hooks/useTagsForEntityType';
import {
  useUserHasFeature,
  useUserHasTableTotalsFeature,
} from 'src/hooks/useUserHasFeature';
import { useServerUserSetting } from 'src/hooks/useUserSetting';
import { FormatOptionEntries } from 'src/modals/EditTableColumns';
import { flattenListingGroup } from 'src/modals/GroupListings/components/groupingUtils';
import { NoData } from 'src/tables/Table';
import { Table } from 'src/tables/TableV2/Table';
import {
  defaultListingFlattenedColumnsConfig,
  filterColumnsByFeatures,
  getListingColumnConfigById,
} from 'src/utils/columns/columnUtils';
import {
  ListingTableColumnId,
  ListingTableFlattenedColumnId,
} from 'src/utils/columns/inventory/inventoryColumnUtils.types';
import { filterCustomColumnsForListing } from 'src/utils/columns/inventory/inventoryCustomColumnUtils';
import { CustomListingColumn } from 'src/utils/columns/inventory/inventoryCustomColumnUtils.types';
import { MAX_NUM_OF_ITEMS_FOR_FLATTENED_VIEWS } from 'src/utils/constants/constants';
import { ContentId } from 'src/utils/constants/contentId';
import { FormatContentId } from 'src/utils/constants/formatContentId';
import { generateShimmeringRows } from 'src/utils/dataTableUtils';
import { SectionType } from 'src/utils/types/sectionType';
import { hasFeatures } from 'src/utils/userUtils';
import { SomethingWentWrong } from 'src/views';
import {
  ActionOutboxEntityType,
  Feature,
  Listing,
  ListingGroup,
  UserSetting,
} from 'src/WebApiController';

import { usePaginationSettings } from '../Table/hooks/usePaginationSettings';
import { RowWrapper } from '../Table/Table.types';
import { ListingPricingForm } from './configs/ListingPriceForm';
import {
  customColumnDef,
  tagColumnDef,
} from './configs/ListingTableColumnsConfig';
import { getListingTableColumnConfigsV2 } from './configs/ListingTableColumnsConfigV2';
import { LISTING_TABLE_FLATTENED_COLUMNS_CONFIG_V2 } from './configs/ListingTableFlattenedColumnsConfigV2';
import { ListingWithEvent } from './ListingTable.types';

export type ListingTableFlattenedV2Props = {
  listings?: ListingWithEvent[];
  listingsSummary?: ListingWithEvent | null;
  listingCount: number;
  ungroupedListingCount: number;
  listingGroupCount: number;
  failedToRetrieveData: boolean;
  onMount?: () => void;
  onUnmount?: (state: object) => void;
  initState?: InitialTableState;
  selectionMode?: SelectionMode | null;
  selectedIds?: number[];
  onItemSelectionsChanged?: (newSelectedIds: number[]) => void;
  disablePagination?: boolean;
  useVirtuoso?: boolean;
  columnVisibilityOverride?: (
    | ListingTableColumnId
    | ListingTableFlattenedColumnId
  )[];
  formatOptionOverrides?: FormatOptionEntries;
  withOuterPadding?: boolean;
  disableSelection?: boolean;
  alwaysShowCheckbox?: boolean;
  showSelectAllOnHeader?: boolean;
  enableColumnFilters?: boolean;
  ignoreMaxCount?: boolean;
  groupingStateReadonly?: GroupingState;
  useDataGrouping?: boolean;
  onPaginationChange?: (pagination: PaginationState) => void;
  onSortingChange?: (sorting: SortingState) => void;
  serverSidePageIndex?: number;
  manualPagination?: boolean;
  rowCount?: number;
  pageSize?: number;
  isSearchV2?: boolean;
};

const PAGE_SIZE = 100;

const getRowId = (originalRow: ListingWithEvent | null, index: number) =>
  (originalRow?.listing?.id ?? index).toString();

export const ListingTableFlattenedV2 = ({
  listings,
  listingsSummary,
  listingCount,
  ungroupedListingCount,
  listingGroupCount,
  failedToRetrieveData,
  selectedIds,
  selectionMode: defaultSelectionMode,
  onItemSelectionsChanged,
  disablePagination,
  onMount,
  onUnmount,
  initState,
  useVirtuoso,
  columnVisibilityOverride,
  formatOptionOverrides,
  withOuterPadding = true,
  disableSelection,
  alwaysShowCheckbox,
  showSelectAllOnHeader,
  enableColumnFilters,
  ignoreMaxCount,
  groupingStateReadonly,
  useDataGrouping = false,
  onPaginationChange: onPaginationChangeCallback,
  onSortingChange: onSortingChangeCallback,
  serverSidePageIndex,
  manualPagination,
  rowCount,
  pageSize,
  isSearchV2,
}: ListingTableFlattenedV2Props) => {
  const hasTableTotalsFeature = useUserHasTableTotalsFeature(
    ActionOutboxEntityType.Listing
  );

  const { appContext, loginContext } = useAppContext();

  const { sorting, setSorting } = useActiveSortTableColumnContext();
  const {
    selectionMode: selectionModeOverride,
    setGroupItems,
    getSelection,
    getGroupToggleState,
    isAllSelected,
    updateGroupFilteredIds,
  } = useMultiSelectionContext();

  const [grouping, setGrouping] = useState<GroupingState>(
    groupingStateReadonly ?? initState?.grouping ?? []
  );

  useEffect(() => {
    if (!isEqual(grouping, groupingStateReadonly ?? [])) {
      setGrouping(groupingStateReadonly ?? []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupingStateReadonly]);

  useDataGrouping = useDataGrouping || groupingStateReadonly != null;

  const selectionMode = selectionModeOverride ?? defaultSelectionMode;

  const listingSelection = getSelection(NO_GROUP_ID);
  const hasSortListingsInGroupFeature = useUserHasFeature(
    Feature.SortListingsInGroup
  );

  useClearShiftSelectionOnSortingChange({ sortingState: sorting });

  const [expandedState, setExpandedState] = useState<ExpandedState>(
    initState?.expanded ?? {}
  );

  // This is to pre-expand all the groups on first-load
  useEffect(() => {
    if (
      Object.keys(expandedState).length === 0 &&
      listings?.some((l) => l.isLtGrp)
    ) {
      setExpandedState(
        listings?.reduce(
          (r, l) => {
            if (l.isLtGrp) {
              r[l.listing!.id.toString()] = true;
            }

            return r;
          },
          {} as Record<string, boolean>
        ) ?? {}
      );
    }
  }, [expandedState, listings]);

  const { inputPriceFocusContext } = useInputPriceFocusContext();
  const { isItemsLoading } = useSearchDataContextWithCatalogFallback();
  const currentPage = useMemo(() => {
    return inputPriceFocusContext &&
      inputPriceFocusContext.rowIndex < listingCount
      ? inputPriceFocusContext.currentPageIndex
      : -1;
  }, [inputPriceFocusContext, listingCount]);

  const { data, hasNoDataLoad } = useMemo(() => {
    const data =
      listings == null || isItemsLoading
        ? (generateShimmeringRows(
            ungroupedListingCount,
            listingGroupCount
          ) as (ListingWithEvent | null)[])
        : listings;

    const hasNoDataLoad =
      !ignoreMaxCount && listingCount > MAX_NUM_OF_ITEMS_FOR_FLATTENED_VIEWS;
    return { data, hasNoDataLoad };
  }, [
    listings,
    isItemsLoading,
    ungroupedListingCount,
    listingGroupCount,
    ignoreMaxCount,
    listingCount,
  ]);

  const { pagination, setPagination } = usePaginationSettings(
    data.length,
    serverSidePageIndex ?? currentPage,
    pageSize ?? PAGE_SIZE,
    disablePagination,
    initState
  );

  useEffect(() => {
    onMount?.();
  });

  useEffect(() => {
    /**
     * Intent for `onMount` is use with 'windowing' in order to maintain user state
     * when the component is scrolled back into view.
     * Anything that needs to be persisted in `react-table` state should be added here.
     * Only update on unmount to ensure we aren't doing too many re-renders.
     */
    return () => onUnmount?.({ pagination, sorting, grouping });
  }, [pagination, sorting, onUnmount, grouping]);

  const rowSelection = useMemo(() => {
    return data
      ?.flatMap((d) => [
        d?.listing,
        ...((d?.listing as ListingGroup)?.groupItems ?? []),
        ...((d?.listing as ListingGroup)?.groupItems ?? []).flatMap(
          (lg) => (lg as ListingGroup).groupItems ?? []
        ),
      ])
      ?.reduce<Record<string, boolean>>((result, listing) => {
        if (listing?.id) {
          const hasClientSideFilter = !!listingSelection?.items.itemIds.length;

          const isAllSelectedWithoutClientSideFilter =
            isAllSelected && !hasClientSideFilter;

          const isGroupSelectedWithoutClientSideFilter =
            getGroupToggleState(NO_GROUP_ID).isGroupSelected &&
            !hasClientSideFilter;

          result[listing.id] = isAllSelectedWithoutClientSideFilter
            ? true
            : ((isGroupSelectedWithoutClientSideFilter ||
                (listingSelection?.items.itemIds ?? selectedIds)?.includes(
                  String(listing?.id)
                )) ??
              false);
        }
        return result;
      }, {});
  }, [
    data,
    getGroupToggleState,
    isAllSelected,
    listingSelection?.items.itemIds,
    selectedIds,
  ]);

  const onRowSelectionChange = useCallback(
    (updater: Updater<RowSelectionState>) => {
      if (disableSelection) return;

      const newSelections =
        typeof updater === 'function' ? updater(rowSelection) : updater;
      const newSelectedIds = Object.keys(newSelections).filter(
        (r) => newSelections[r] === true
      );
      if (selectionMode?.mode === 'singleItem') {
        newSelectedIds.forEach((id) => {
          if (rowSelection[id] && newSelections[id]) {
            // if both of these are the same value and is true, we need to set it to false
            newSelections[id] = false;
          }
        });
      }
      setGroupItems(NO_GROUP_ID, newSelections);
      onItemSelectionsChanged?.(
        newSelectedIds
          .filter((id) => newSelections[id])
          .map((id) => parseInt(id))
      );
    },
    [
      disableSelection,
      rowSelection,
      selectionMode?.mode,
      setGroupItems,
      onItemSelectionsChanged,
    ]
  );

  const onPaginationChange = useCallback(
    (updater: Updater<PaginationState>) => {
      const newPagination = functionalUpdate(updater, pagination);
      setPagination(newPagination);
      onPaginationChangeCallback?.(newPagination);
    },
    [setPagination, pagination, onPaginationChangeCallback]
  );

  const onSortingChange = useCallback(
    (updater: Updater<SortingState>) => {
      const newSorting = functionalUpdate(updater, sorting);
      setSorting(newSorting);
      onSortingChangeCallback?.(newSorting);
    },
    [setSorting, sorting, onSortingChangeCallback]
  );

  const {
    value:
      storedInventoryFlattenedColumnOrderSetting = defaultListingFlattenedColumnsConfig,
  } = useServerUserSetting<ListingTableColumnId[]>({
    id: UserSetting.InventoryFlattenedColumnOrder,
  });
  const { value: storedInventoryColumnsEnabledSetting } = useServerUserSetting<
    string[]
  >({
    id: UserSetting.InventoryFlattenedColumnsEnabled,
  });

  const { value: customListingColumns = [] } = useServerUserSetting<
    CustomListingColumn[]
  >({
    id: UserSetting.InventoryCustomColumns,
  });
  const { tagsMetadata, tagsMetadataNumeric } = useTagsForEntityType(
    ActionOutboxEntityType.Listing,
    true
  );

  const customListingColumnsFiltered = filterCustomColumnsForListing(
    customListingColumns,
    tagsMetadataNumeric
  );

  const showCheckbox = useMemo(
    () =>
      alwaysShowCheckbox ||
      selectionMode?.mode === MultiSelectScope.AllGroups ||
      listingSelection.isSingleGroupMultiSelect,
    [
      alwaysShowCheckbox,
      listingSelection.isSingleGroupMultiSelect,
      selectionMode?.mode,
    ]
  );

  const { value: editableColumns = [] } = useServerUserSetting<string[]>({
    id: UserSetting.InventoryColumnEditability,
  });

  const editableColumnsSet = useMemo(
    () => new Set(editableColumns),
    [editableColumns]
  );

  const allColumnConfigs = useMemo(
    () =>
      getListingTableColumnConfigsV2(
        hasSortListingsInGroupFeature,
        false,
        false,
        editableColumnsSet
      ),
    [editableColumnsSet, hasSortListingsInGroupFeature]
  );

  const seatingColumnSorting = useSortListingTableSeatingColumn(
    SectionType.ListingsFlattened,
    useDataGrouping
  );

  const displayedColumnsConfig = useMemo(() => {
    const inventoryColumnOrder = columnVisibilityOverride
      ? columnVisibilityOverride
      : storedInventoryFlattenedColumnOrderSetting.filter(
          // If storedInventoryColumnsEnabledSetting has never been set, default to true
          (c) => storedInventoryColumnsEnabledSetting?.includes(c) ?? true
        );

    const listingTableColumnConfigs = Object.values(allColumnConfigs);
    const listingTableFlattenedColumnConfigs = Object.values(
      LISTING_TABLE_FLATTENED_COLUMNS_CONFIG_V2
    );

    const columns = new Set(
      columnVisibilityOverride
        ? columnVisibilityOverride
            .map(
              (cid) =>
                listingTableColumnConfigs.find((c) => c.id === cid) ??
                listingTableFlattenedColumnConfigs.find((c) => c.id === cid)!
            )
            .map((item) => item.id!)
        : [
            // Add unconfigurable columns first
            ...listingTableFlattenedColumnConfigs
              .filter((column) => {
                const columnDef = getListingColumnConfigById(
                  column.id as ListingTableFlattenedColumnId
                ).personalization;
                const hasFeatureForColumn =
                  columnDef.requiredFeatures.length === 0 ||
                  hasFeatures(
                    loginContext?.user,
                    appContext?.features,
                    columnDef.requiredFeatures
                  );

                return !columnDef.isConfigurable && hasFeatureForColumn;
              })
              .map((item) => item.id!),
            ...listingTableColumnConfigs
              .filter((column) => {
                const columnDef = getListingColumnConfigById(
                  column.id as ListingTableColumnId
                ).personalization;
                const hasFeatureForColumn =
                  columnDef.requiredFeatures.length === 0 ||
                  hasFeatures(
                    loginContext?.user,
                    appContext?.features,
                    columnDef.requiredFeatures
                  );

                const isConfigurable = columnDef.isConfigurable;
                return !isConfigurable && hasFeatureForColumn;
              })
              .map((item) => item.id!),
            // Add user defined columns next
            ...filterColumnsByFeatures(
              inventoryColumnOrder,
              SectionType.ListingsFlattened,
              customListingColumnsFiltered,
              loginContext?.user,
              appContext?.features,
              tagsMetadata ?? []
            ),
          ]
    );

    if (!columnVisibilityOverride) {
      if (showCheckbox) {
        if (!alwaysShowCheckbox) {
          columns.delete(ListingTableColumnId.Actions);
        }
      } else {
        columns.delete(ListingTableColumnId.Checkbox);
      }
    }

    if (isSearchV2) {
      // Deprecated. Fully cleanup in POS-7523
      columns.delete(ListingTableColumnId.MarketplaceVisibility);
    }

    return Array.from(columns).reduce<ColumnDef<ListingWithEvent>[]>(
      (acc, columnId) => {
        if (
          !columnVisibilityOverride &&
          tagsMetadata?.find((t) => t.key === columnId)
        ) {
          acc.push(tagColumnDef(columnId) as ColumnDef<ListingWithEvent>);
        } else if (
          !columnVisibilityOverride &&
          customListingColumnsFiltered.find((c) => c.id === columnId)
        ) {
          acc.push(
            customColumnDef(
              columnId,
              customListingColumnsFiltered.find((c) => c.id === columnId)
                ?.formula
            ) as ColumnDef<ListingWithEvent>
          );
        } else {
          const columnConfig1 = listingTableColumnConfigs.find(
            (column) => column.id === columnId
          );
          if (columnConfig1) {
            acc.push(columnConfig1 as ColumnDef<ListingWithEvent>);
          }
          const columnConfig2 = listingTableFlattenedColumnConfigs.find(
            (column) => column.id === columnId
          );
          if (columnConfig2) {
            acc.push(columnConfig2 as ColumnDef<ListingWithEvent>);
          }
        }

        return acc;
      },
      []
    );
  }, [
    columnVisibilityOverride,
    storedInventoryFlattenedColumnOrderSetting,
    allColumnConfigs,
    customListingColumnsFiltered,
    loginContext?.user,
    appContext?.features,
    tagsMetadata,
    isSearchV2,
    storedInventoryColumnsEnabledSetting,
    showCheckbox,
    alwaysShowCheckbox,
  ]);

  const [visibleRowIds, setVisibleRowIds] = useState<number[]>();

  const listingsSummaryFiltered = useMemo(() => {
    if (!listingsSummary || !hasTableTotalsFeature) {
      return null;
    }

    if (visibleRowIds) {
      return createAggregatedListing(
        data
          ?.filter((l) => !l?.isCompListing)
          ?.flatMap((l) => flattenListingGroup(l?.listing))
          .filter((l) => l?.isFull)
          .map((l) => ({
            listing: l as Listing,
            event: data[0]!.event,
          }))
          ?.filter(
            (d, i) =>
              d?.listing && visibleRowIds.includes(Number(getRowId(d, i)))
          )
          .map((d) => d!)
      );
    }
    return listingsSummary;
  }, [visibleRowIds, data, listingsSummary, hasTableTotalsFeature]);

  const tableMeta = useMemo(
    () => ({
      sectionType: SectionType.ListingsFlattened,
      showSelectAllCheckbox: showSelectAllOnHeader,
      summaryData: listingsSummaryFiltered,
    }),
    [showSelectAllOnHeader, listingsSummaryFiltered]
  );

  const onRowWrapper: RowWrapper<ListingWithEvent | null> = useCallback(
    (rowData, tableRow, rowIndex) => {
      return (
        <ListingPricingForm
          key={rowIndex}
          rowData={
            rowData && {
              ...rowData,
              rowIndex,
              currentPageIndex: pagination.pageIndex,
            }
          }
          tableRow={tableRow}
        />
      );
    },
    [pagination.pageIndex]
  );

  // Memoize heavy state objects separately
  const tableState = useMemo(
    () => ({
      pagination,
      sorting,
      rowSelection,
      expanded: expandedState,
      columnVisibility: {},
    }),
    [pagination, sorting, rowSelection, expandedState]
  );

  return failedToRetrieveData ? (
    <SomethingWentWrong
      message={<Content id={ContentId.FailToLoadListContent} />}
    />
  ) : hasNoDataLoad ? (
    <NoData>
      <WarningMessage
        message={
          <FormatContent
            id={FormatContentId.ApplyFilterToReduceCountsToSeeData}
            params={[MAX_NUM_OF_ITEMS_FOR_FLATTENED_VIEWS.toString()]}
          />
        }
      />
    </NoData>
  ) : data?.length > 0 ? (
    <ColumnResizingContextProvider
      userSettingId={UserSetting.InventoryColumnWidths}
    >
      <Table<ListingWithEvent>
        actionColId={ListingTableColumnId.Actions}
        data={data as ListingWithEvent[]}
        columns={displayedColumnsConfig}
        alwaysShowCheckbox={alwaysShowCheckbox}
        manualPagination={manualPagination}
        rowCount={rowCount}
        rowWrapper={onRowWrapper}
        useVirtuoso={useVirtuoso}
        state={tableState}
        onRowSelectionChange={onRowSelectionChange}
        pagination={
          disablePagination
            ? undefined
            : { pageSize: pagination.pageSize, variant: 'input' }
        }
        onPaginationChange={onPaginationChange}
        onSortingChange={onSortingChange}
        meta={tableMeta}
        getGroupId={(data) => NO_GROUP_ID}
        getItemId={(data) => String(data?.listing?.id)}
        sortingFns={{ ...seatingColumnSorting }}
      />
    </ColumnResizingContextProvider>
  ) : null;
};
